import { useCallback } from 'react';
import { useQuery } from 'react-query';
import produce from 'immer';

import { useCurrentAddressInfo } from '@/hooks';
import { request, useCurrentUserId, queryClient, formatNum } from '@/utils';

import { Badge, BadgeMintRecord } from '../types';
import { transBadgeMintRecord } from '../utils';
import { useBadgeMintedCount } from '../state';
import { useIsOfficialBadge } from './official';

type BadgeMintUsersRes = {
  mint_list: BadgeMintRecord[];
  total_count: number;
  _totalCount: string;
};

export const useBadgeMintUsers = (id: string, start = 0, order_by?: string) => {
  const currentAddress = useCurrentUserId();
  const isOfficialBadge = useIsOfficialBadge();
  const url = isOfficialBadge ? '/official_badge/mint_list' : '/badge/mint_list';

  const [, setBadgeMintedCount] = useBadgeMintedCount();

  const select = useCallback(({ total_count, mint_list }: BadgeMintUsersRes) => {
    return {
      total_count,
      _totalCount: formatNum(total_count, 0),
      mint_list: mint_list.map(transBadgeMintRecord),
    };
  }, []);

  return useQuery(
    ['badge-mint-list', currentAddress, url, start, order_by],
    () => {
      const params = {
        id,
        start,
        order_by,
        limit: 100,
      };

      return request.get<BadgeMintUsersRes>(url, params);
    },
    {
      select,
      onSuccess: res => {
        setBadgeMintedCount(res._totalCount);
      },
    },
  );
};

type UserBadgesRes = {
  total_count: number;
  _totalCount: string;
  badge_list: Badge[];
};

export const useUserBadges = (userId: string) => {
  const url = '/user/badge_list';

  const select = useCallback((res: UserBadgesRes) => {
    res._totalCount = formatNum(res.total_count, 0);

    return res;
  }, []);

  return useQuery([url, userId], () => request.get<UserBadgesRes>(url, { id: userId }), { select });
};

export const useUserBadgeCount = (userId: string) => {
  const url = '/user/badge_count';

  return useQuery([url, userId], () => request.get<{ badge_count: number }>(url, { id: userId }));
};

export const useMinterFollower = () => {
  const { userInfo, address } = useCurrentAddressInfo();

  return ({ id, isFollowing }: { id: string; isFollowing: boolean }) => {
    queryClient.setQueriesData<BadgeMintUsersRes | undefined>(['badge-mint-list', address], pre => {
      if (!pre) {
        return pre;
      }

      return produce(pre, draft => {
        const record = draft?.mint_list?.find(x => x.id === id);

        if (record) {
          record.user.is_following = isFollowing;

          record.user.follower_count = isFollowing
            ? record.user.follower_count + 1
            : record.user.follower_count > 1
              ? record.user.follower_count - 1
              : 0;

          const usdValue = userInfo?.desc?.usd_value || 0;
          record.tvf = isFollowing ? record.tvf + usdValue : record.tvf - usdValue;

          if (record.tvf < 0) {
            record.tvf = 0;
          }
        }
      });
    });
  };
};
